import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Helmet } from "react-helmet";

import { HeroSection } from "./components";

// Lazy load non-critical components to reduce initial bundle size
const FeaturesSection = lazy(
  () => import("./components/FeaturesSection")
);
const TestimonialSection = lazy(
  () => import("./components/TestimonialSection")
);
const WorkflowSection = lazy(() => import("./components/WorkflowSection"));
const PricingSection = lazy(() => import("./components/PricingSection"));
const FAQSection = lazy(() => import("./components/FAQSection"));
const CallToActionBanner = lazy(
  () => import("./components/CallToActionBanner")
);
const SupportAssistant = lazy(() => import("./components/SupportAssistant"));

import { AuthOverlayDialog } from "@/components/ui/AuthOverlayDialog";
import { ChatProvider } from "@/contexts/ChatContext";
import { useScrollToTop } from "@/hooks/common";
import { useMobileOptimized } from "@/hooks/useMobileOptimized";
import {
  optimizeMainThread,
  scheduleIdleTask,
} from "@/utils/mainThreadOptimizer";
import {
  optimizeForLandingPage,
  preloadLandingPageResources,
} from "@/utils/preloadOptimizer";

// Optimized animations - reduced complexity
const customAnimations = `
  @keyframes gradient-slow {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }

  .animate-gradient-slow {
    background-size: 200% 200%;
    animation: gradient-slow 15s ease infinite;
  }
`;

// Lightweight loading component for lazy-loaded sections
const SectionLoader = ({ height = "400px" }) => (
  <div
    className="flex items-center justify-center bg-background/50 animate-pulse"
    style={{ minHeight: height }}
  >
    <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

export function LandingPage() {
  const containerRef = useRef(null);
  const { isMobile, isLowEndDevice, performanceSettings } =
    useMobileOptimized();

  // Auth overlay state
  const [showAuthOverlay, setShowAuthOverlay] = useState(false);
  const [authOverlayFeature, setAuthOverlayFeature] = useState("");

  // Handle showing auth overlay for restricted features
  const handleShowAuthOverlay = useCallback((featureName) => {
    setAuthOverlayFeature(featureName);
    setShowAuthOverlay(true);
  }, []);

  // Scroll to top when component mounts
  useScrollToTop(true);

  // Comprehensive performance optimization
  useEffect(() => {
    // Initialize all performance optimizations immediately
    optimizeForLandingPage();
    optimizeMainThread();

    // Preload critical resources with high priority
    preloadLandingPageResources();

    // Schedule non-critical tasks during idle time
    scheduleIdleTask(() => {
      // Additional critical images for immediate preload
      const criticalImages = ["/hero-banner.jpg", "/assets/og-image.jpeg"];
      criticalImages.forEach((src) => {
        const link = document.createElement("link");
        link.rel = "preload";
        link.as = "image";
        link.href = src;
        link.fetchPriority = "high";
        document.head.appendChild(link);
      });
    }, "high");

    scheduleIdleTask(() => {
      // Preload critical fonts - using Google Fonts instead of local files
      const fontLink = document.createElement("link");
      fontLink.rel = "preload";
      fontLink.href =
        "https://fonts.gstatic.com/s/spacegrotesk/v15/V8mQoQDjQSkFtoMM3T6r8E7mF71Q-gOoraIAEj7oUXskPMBBSSJLm2E.woff2";
      fontLink.as = "font";
      fontLink.type = "font/woff2";
      fontLink.crossOrigin = "anonymous";
      document.head.appendChild(fontLink);
    }, "high");

    // Performance monitoring and adaptive optimization
    if ("performance" in window) {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          // Monitor JavaScript execution time
          if (entry.entryType === "measure" && entry.duration > 50) {
            console.warn(
              `Long task detected: ${entry.name} took ${entry.duration}ms`
            );
          }

          // Monitor navigation timing
          if (entry.entryType === "navigation") {
            const jsExecutionTime =
              entry.domContentLoadedEventEnd - entry.domContentLoadedEventStart;
            console.log(`JavaScript execution time: ${jsExecutionTime}ms`);

            // If JS execution is slow, apply emergency optimizations
            if (jsExecutionTime > 1000) {
              document.documentElement.style.setProperty(
                "--animation-duration",
                "0.1s"
              );
              document.documentElement.classList.add(
                "emergency-performance-mode"
              );
            }
          }
        });
      });

      try {
        observer.observe({ entryTypes: ["measure", "navigation"] });
      } catch (e) {
        console.log("Performance monitoring not fully supported");
      }

      // Cleanup observer
      return () => {
        observer.disconnect();
      };
    }
  }, []);

  return (
    <ChatProvider>
      <Helmet>
        <title>
          Artifact - Access ChatGPT, Claude & All AI Models in One Platform
        </title>
        <meta
          name="description"
          content="Unite all leading AI models in one workspace. Chat with ChatGPT, Claude, Gemini & more. Boost productivity with the ultimate AI assistant platform. Start your free trial today!"
        />

        {/* Keywords */}
        <meta
          name="keywords"
          content="AI assistant, ChatGPT alternative, Claude AI, Gemini AI, multiple AI models, AI chatbot, unified AI platform, AI workspace, productivity AI, best AI assistant, AI comparison, OpenAI alternative"
        />

        {/* Canonical URL */}
        <link rel="canonical" href="https://artifact.chat/" />

        {/* Enhanced preconnect for performance */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin="anonymous"
        />
        <link rel="dns-prefetch" href="https://fonts.googleapis.com" />
        <link rel="dns-prefetch" href="https://fonts.gstatic.com" />

        {/* Critical resource hints for faster loading */}
        <link
          rel="preload"
          href="https://fonts.gstatic.com/s/spacegrotesk/v15/V8mQoQDjQSkFtoMM3T6r8E7mF71Q-gOoraIAEj7oUXskPMBBSSJLm2E.woff2"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link rel="preload" href="/assets/og-image.jpeg" as="image" />

        {/* Open Graph Meta Tags */}
        <meta
          property="og:title"
          content="Artifact - Access ChatGPT, Claude & All AI Models in One Platform"
        />
        <meta
          property="og:description"
          content="Unite all leading AI models in one workspace. Chat with ChatGPT, Claude, Gemini & more. Boost productivity with the ultimate AI assistant platform."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://artifact.chat/" />
        <meta
          property="og:image"
          content="https://artifact.chat/assets/og-image.jpeg"
        />
        <meta property="og:site_name" content="Artifact" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Artifact - Access GPT-4, Claude & All AI Models in One Platform"
        />
        <meta
          name="twitter:description"
          content="Unite all leading AI models in one workspace. Chat with ChatGPT, Claude, Gemini & more. Boost productivity with the ultimate AI assistant platform."
        />
        <meta
          name="twitter:image"
          content="https://artifact.chat/assets/og-image.jpeg"
        />
        <meta name="twitter:site" content="@ArtifactAI" />
        <meta name="twitter:creator" content="@ArtifactAI" />

        {/* Additional Meta Tags */}
        <meta name="author" content="Artifact" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Artifact",
            description:
              "Unite all leading AI models in one workspace. Chat with ChatGPT, Claude, Gemini & more. Boost productivity with the ultimate AI assistant platform.",
            url: "https://artifact.chat",
            applicationCategory: "ProductivityApplication",
            operatingSystem: "Web Browser",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "USD",
              description: "Free tier available",
            },
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: "4.8",
              reviewCount: "1250",
            },
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background" ref={containerRef}>
        {/* Only add custom animations on non-mobile devices */}
        {!isMobile && !isLowEndDevice && (
          <style dangerouslySetInnerHTML={{ __html: customAnimations }} />
        )}

        {/* Critical above-the-fold content - loaded immediately */}
        <HeroSection onShowAuthOverlay={handleShowAuthOverlay} />

        <Suspense fallback={<SectionLoader height="400px" />}>
          <WorkflowSection />
        </Suspense>

        {/* Lazy-loaded sections with optimized loading */}
        <Suspense fallback={<SectionLoader height="500px" />}>
          <FeaturesSection />
        </Suspense>

        <Suspense fallback={<SectionLoader height="200px" />}>
          <TestimonialSection />
        </Suspense>

        <Suspense fallback={<SectionLoader height="600px" />}>
          <PricingSection />
        </Suspense>

        <Suspense fallback={<SectionLoader height="400px" />}>
          <FAQSection />
        </Suspense>

        <Suspense fallback={<SectionLoader height="300px" />}>
          <CallToActionBanner />
        </Suspense>

        <Suspense fallback={<SectionLoader height="200px" />}>
          <SupportAssistant />
        </Suspense>

        {/* Auth Overlay Dialog for restricted features */}
        <AuthOverlayDialog
          isOpen={showAuthOverlay}
          onClose={() => setShowAuthOverlay(false)}
          featureName={authOverlayFeature}
        />
      </div>
    </ChatProvider>
  );
}

export default LandingPage;
